import React, { useEffect, useState, useCallback } from "react";
import {
  pdfViewerPageWidth,
  pdfViewerStyles,
} from "../../../theme/pdfViewerStyles";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Button } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import {
  downloadPdf,
  generatePdf,
  getDownloadUrl,
} from "../fetchers/generatePdf";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import useUser from "../../../firebase/useUser";
import { useAuth } from "../../../firebase/useAuth";
import useUserMetadata from "../../../firebase/useUserMetadata";
import usePrepFqProfile from "../fetchers/usePrepFqProfile";
import logging from "../../../utils/logging";
import { PwgError } from "../../../models/common";
import { useRouter } from "next/router";
import { doc, getFirestore, updateDoc } from "@firebase/firestore";
import { firebaseApp } from "../../../firebase/firebaseClient";

const PdfViewer = dynamic(() => import("../../../components/PdfViewer"), {
  ssr: false,
});

const PdfViewerRoot = () => {
  const classes = pdfViewerStyles();
  const { t } = useTranslation(["prepFq"]);
  const [downloadUrl, setDownloadUrl] = useState("");
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { userMetadata } = useUserMetadata(authUser?.user?.uid);
  const { profile } = usePrepFqProfile(authUser?.user?.uid);
  const [width, setWidth] = useState(pdfViewerPageWidth);
  const router = useRouter();
  const dispatch = useDispatch();
  const fetchPdf = useCallback(async () => {
    const firestore = getFirestore(firebaseApp);
    if (user?.user_id && userMetadata && profile) {
      try {
        if (
          !profile?.date_pdf_last_generated ||
          profile?.date_pdf_last_generated < profile?.date_last_updated
        ) {
          const dlUrl = await generatePdf(user.user_id);
          setDownloadUrl(dlUrl);
          await updateDoc(
            doc(firestore, "prep_fq_profiles", profile.prep_fq_profile_id),
            {
              date_pdf_last_generated: new Date().getTime(),
            }
          );
        } else {
          const dlUrl = await getDownloadUrl(user.user_id);
          setDownloadUrl(dlUrl);
        }
      } catch (error) {
        const pwgError: PwgError = {
          user_id: authUser?.user?.uid,
          ssr: false,
          context: "fetchPdf: PdfViewer",
          date_created: new Date().getTime(),
          error: error?.toString(),
          pathname: router.asPath,
        };
        await logging.e(
          "🚀 ~ file: PdfViewer.tsx ~ line 73 ~ fetchPdf ~ error",
          error,
          pwgError
        );
      }
    }
  }, [
    user?.user_id,
    profile,
    userMetadata,
    authUser?.user?.uid,
    router.asPath,
  ]);
  useEffect(() => {
    fetchPdf();
  }, [user?.user_id, profile, userMetadata, fetchPdf]);
  useEffect(() => {
    if (typeof window !== undefined) {
      window.addEventListener("resize", handleResize);
      handleResize();
    }
    return () => {
      if (typeof window !== undefined) {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, []);
  const handleResize = () => {
    if (typeof window !== undefined) {
      if (window.innerWidth > pdfViewerPageWidth) {
        setWidth(pdfViewerPageWidth - 48);
      } else {
        setWidth(window.innerWidth - 18);
      }
    }
  };
  const download = () => {
    downloadPdf(
      dispatch,
      t,
      user,
      downloadUrl,
      authUser?.user?.uid,
      router.asPath
    );
  };
  return (
    <React.Fragment>
      <section className={classes.root}>
        <PdfViewer
          user_id={authUser?.user?.uid}
          width={width}
          pageNumber={1}
          numPages={1}
          docClasses={classes.page}
          noData={
            <div className={classes.loader}>
              <CircularProgress style={{ color: "white" }} size={64} />
              <p className={classes.loaderText}>
                {t("common:loading.prepFqReport")}
              </p>
            </div>
          }
          loading={
            <div className={classes.loader}>
              <CircularProgress style={{ color: "white" }} size={64} />
              <p className={classes.loaderText}>
                {t("common:loading.prepFqReport")}
              </p>
            </div>
          }
          error={
            <div className={classes.loader}>
              <p className={classes.loaderText}>
                {t("common:errors.prepFqReport")}
              </p>
            </div>
          }
          url={downloadUrl}
        />
        <div
          className={classes.buttonsContainer}
          style={{ width: width < pdfViewerPageWidth ? width - 16 : undefined }}
        >
          <Button
            onClick={download}
            disabled={downloadUrl === ""}
            className={clsx(
              classes.downloadButton,
              classes.dlButton,
              "dl-button-overlay"
            )}
            variant="contained"
            color="secondary"
          >
            <GetAppIcon className={classes.downloadIcon} />
            &nbsp;{t("common:buttons.download")}
          </Button>
          <Button
            href={downloadUrl}
            target="_blank"
            rel="noreferrer noopener"
            disabled={downloadUrl === ""}
            className={clsx(
              classes.downloadButton,
              classes.dlButton,
              "dl-button-overlay"
            )}
            variant="contained"
            color="secondary"
          >
            <OpenInNewIcon className={classes.downloadIcon} />
            &nbsp;{t("common:buttons.open")}
          </Button>
        </div>
      </section>
    </React.Fragment>
  );
};

export default PdfViewerRoot;
